import { runtimeConfig } from '@/config/runtime-config'
import { createApiClient } from '@/lib/api/http-client'
import { getStoredToken } from '@/lib/auth/token-storage'

export type WecomIdentityProfileStatus = 'active' | 'disabled'
export type WecomIdentityReadinessStatus = 'blocked' | 'ready' | 'unknown'

export interface WecomIdentityReadiness {
  action_required: string[]
  checked_at?: string | null
  items: Record<
    string,
    {
      action_required?: string | null
      status: WecomIdentityReadinessStatus
    }
  >
  status: WecomIdentityReadinessStatus
}

export interface WecomIdentityProfile {
  email_binding_policy?: WecomEmailPolicy
  email_binding_policy_version?: number
  active_binding_count: number
  agent_id: string
  corp_id?: string | null
  corp_id_masked: string
  created_at?: string | null
  display_name: string
  has_app_secret: boolean
  id: string
  integration_ref_count: number
  readiness: WecomIdentityReadiness
  secret_version: number
  status: WecomIdentityProfileStatus
  updated_at?: string | null
}

export interface WecomIdentityProfileCreatePayload {
  agent_id: string
  app_secret: string
  corp_id: string
  display_name: string
}

export interface WecomIdentityProfileUpdatePayload {
  display_name: string
}

interface SuccessEnvelope<T> {
  code: 'success'
  data: T
}

interface MessageEnvelope {
  code: 'success'
  message: string
}

const apiClient = createApiClient({
  baseUrl: runtimeConfig.apiBaseUrl,
  getToken: () => getStoredToken('admin'),
  portal: 'admin',
})

const profilesPath = '/admin/wecom-identity-profiles'

function profilePath(profileId: string, suffix = '') {
  return `${profilesPath}/${encodeURIComponent(profileId)}${suffix}`
}

export const wecomIdentityProfilesQueryKey = ['admin-wecom-identity-profiles'] as const

export async function listWecomIdentityProfiles() {
  const response =
    await apiClient.request<SuccessEnvelope<{ items: WecomIdentityProfile[] }>>(profilesPath)

  return response.data.items
}

export async function createWecomIdentityProfile(values: WecomIdentityProfileCreatePayload) {
  const response = await apiClient.request<SuccessEnvelope<WecomIdentityProfile>>(profilesPath, {
    body: JSON.stringify(values),
    method: 'POST',
  })

  return response.data
}

export async function updateWecomIdentityProfile({
  profileId,
  values,
}: {
  profileId: string
  values: WecomIdentityProfileUpdatePayload
}) {
  const response = await apiClient.request<SuccessEnvelope<WecomIdentityProfile>>(
    profilePath(profileId),
    {
      body: JSON.stringify(values),
      method: 'PUT',
    },
  )

  return response.data
}

export async function rotateWecomIdentityProfileSecret({
  appSecret,
  expectedSecretVersion,
  profileId,
}: {
  appSecret: string
  expectedSecretVersion: number
  profileId: string
}) {
  const response = await apiClient.request<SuccessEnvelope<WecomIdentityProfile>>(
    profilePath(profileId, '/rotate'),
    {
      body: JSON.stringify({
        app_secret: appSecret,
        expected_secret_version: expectedSecretVersion,
      }),
      method: 'POST',
    },
  )

  return response.data
}

export async function validateWecomIdentityProfileDraft(values: WecomIdentityProfileCreatePayload) {
  const response = await apiClient.request<SuccessEnvelope<WecomIdentityReadiness>>(
    `${profilesPath}/validate`,
    {
      body: JSON.stringify(values),
      method: 'POST',
    },
  )

  return response.data
}

export async function validateWecomIdentityProfile(profileId: string, appSecret?: string) {
  const response = await apiClient.request<SuccessEnvelope<WecomIdentityReadiness>>(
    profilePath(profileId, '/validate'),
    {
      body: JSON.stringify(appSecret?.trim() ? { app_secret: appSecret.trim() } : {}),
      method: 'POST',
    },
  )

  return response.data
}

export async function setWecomIdentityProfileStatus({
  enabled,
  profileId,
}: {
  enabled: boolean
  profileId: string
}) {
  const response = await apiClient.request<SuccessEnvelope<WecomIdentityProfile>>(
    profilePath(profileId, enabled ? '/enable' : '/disable'),
    { method: 'POST' },
  )

  return response.data
}

export async function deleteWecomIdentityProfile(profileId: string) {
  await apiClient.request<MessageEnvelope>(profilePath(profileId), { method: 'DELETE' })
}

export interface WecomEmailPolicy {
  mode: 'manual' | 'email_match'
  email_field: 'email' | 'biz_mail' | null
  allowed_domains: string[]
  trust_basis: 'enterprise_directory' | null
}
/** 策略版本由服务端返回，冲突后必须重新读取。 */
export async function saveWecomEmailPolicy(
  profileId: string,
  policy: WecomEmailPolicy,
  version: number,
) {
  const response = await apiClient.request<SuccessEnvelope<WecomIdentityProfile>>(
    profilePath(profileId, '/email-binding-policy'),
    { method: 'PUT', body: JSON.stringify({ policy, expected_policy_version: version }) },
  )
  return response.data
}
